import React, { useState } from 'react'
import styles from './BookingSection.module.css'
import { validateName, validateMobileNumber } from '@/utils/validation'

interface Step1DetailsProps {
  initialName: string
  initialMobile: string
  isLoading: boolean
  errorMessage: string | null
  onSubmit: (name: string, mobileNumber: string) => void
  onClearError: () => void
}

export const Step1Details: React.FC<Step1DetailsProps> = ({
  initialName,
  initialMobile,
  isLoading,
  errorMessage,
  onSubmit,
  onClearError,
}) => {
  const [name, setName] = useState(initialName)
  const [mobile, setMobile] = useState(initialMobile)
  const [nameError, setNameError] = useState<string | null>(null)
  const [mobileError, setMobileError] = useState<string | null>(null)
  const [touched, setTouched] = useState({ name: false, mobile: false })

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.toLocaleUpperCase()
    setName(val)
    if (errorMessage) onClearError()
    if (touched.name) {
      const check = validateName(val)
      setNameError(check.isValid ? null : check.error || null)
    }
  }

  const handleMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and limit to 10 digits
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10)
    setMobile(digitsOnly)
    if (errorMessage) onClearError()
    if (touched.mobile) {
      const check = validateMobileNumber(digitsOnly)
      setMobileError(check.isValid ? null : check.error || null)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    setTouched({ name: true, mobile: true })
    const nameCheck = validateName(name)
    const mobileCheck = validateMobileNumber(mobile)

    setNameError(nameCheck.isValid ? null : nameCheck.error || null)
    setMobileError(mobileCheck.isValid ? null : mobileCheck.error || null)

    if (nameCheck.isValid && mobileCheck.isValid) {
      onSubmit(name.trim(), mobile.trim())
    }
  }

  return (
    <form className={styles.formCard} onSubmit={handleSubmit} noValidate>
      <div className={styles.formHeader}>
        <div className={styles.stepBadge}>STEP 1 OF 2</div>
        <h2 className={styles.formTitle}>Book an Appointment</h2>
        <p className={styles.formSubtitle}>
          Enter your details below to receive a secure one-time verification code (OTP).
        </p>
      </div>

      {/* Global Error Banner */}
      {errorMessage && (
        <div className={styles.errorAlert} role="alert">
          <div className={styles.errorAlertIcon} aria-hidden="true">⚠️</div>
          <div className={styles.errorAlertContent}>
            <p className={styles.errorAlertTitle}>Unable to Process Request</p>
            <p className={styles.errorAlertDesc}>{errorMessage}</p>
          </div>
          <button
            type="button"
            className={styles.errorAlertClose}
            onClick={onClearError}
            aria-label="Dismiss error"
          >
            ✕
          </button>
        </div>
      )}

      <div className={styles.fieldGroup}>
        {/* Field 1: Patient Name */}
        <div className={styles.inputWrapper}>
          <label htmlFor="patient-name" className={styles.label}>
            Full Name <span className={styles.requiredAsterisk}>*</span>
          </label>
          <div className={styles.inputContainer}>
            <span className={styles.inputIcon} aria-hidden="true">👤</span>
            <input
              id="patient-name"
              type="text"
              className={`${styles.input} ${nameError ? styles.inputError : ''}`}
              placeholder="e.g. Ramesh Kumar"
              value={name}
              onChange={handleNameChange}
              onBlur={() => {
                setTouched((prev) => ({ ...prev, name: true }))
                const check = validateName(name)
                setNameError(check.isValid ? null : check.error || null)
              }}
              disabled={isLoading}
              autoComplete="name"
              required
              aria-invalid={!!nameError}
              aria-describedby={nameError ? 'patient-name-error' : undefined}
            />
          </div>
          {nameError && (
            <p id="patient-name-error" className={styles.fieldErrorMessage} role="alert">
              {nameError}
            </p>
          )}
        </div>

        {/* Field 2: Mobile Number */}
        <div className={styles.inputWrapper}>
          <label htmlFor="patient-mobile" className={styles.label}>
            Mobile Number <span className={styles.requiredAsterisk}>*</span>
          </label>
          <div className={styles.inputContainer}>
            <span className={styles.countryCode}>+91</span>
            <input
              id="patient-mobile"
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              className={`${styles.input} ${styles.inputWithPrefix} ${
                mobileError ? styles.inputError : ''
              }`}
              placeholder="10-digit mobile number"
              value={mobile}
              onChange={handleMobileChange}
              onBlur={() => {
                setTouched((prev) => ({ ...prev, mobile: true }))
                const check = validateMobileNumber(mobile)
                setMobileError(check.isValid ? null : check.error || null)
              }}
              disabled={isLoading}
              autoComplete="tel-national"
              maxLength={10}
              required
              aria-invalid={!!mobileError}
              aria-describedby={mobileError ? 'patient-mobile-error' : undefined}
            />
          </div>
          {mobileError ? (
            <p id="patient-mobile-error" className={styles.fieldErrorMessage} role="alert">
              {mobileError}
            </p>
          ) : (
            <p className={styles.fieldHint}>
              We will send an OTP via SMS to verify your appointment.
            </p>
          )}
        </div>
      </div>

      {/* Submit Button: Send OTP */}
      <button
        type="submit"
        className={styles.primaryButton}
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <span className={styles.spinner} aria-hidden="true" />
            <span>Sending Verification OTP...</span>
          </>
        ) : (
          <>
            <span>Send OTP</span>
            <span className={styles.buttonArrow} aria-hidden="true">→</span>
          </>
        )}
      </button>

      <div className={styles.privacyNote}>
        <span aria-hidden="true">🔒</span> Your personal data is encrypted and kept 100% secure.
      </div>
    </form>
  )
}

